<template>
  <Modal
    v-model="visible"
    width="800"
    :title="$t('alarms.common.configSopTitle')"
    :mask-closable="false"
    footer-hide
  >
    <div v-if="loading" style="text-align: center; padding: 40px 0;">
      <Spin size="large" fix></Spin>
    </div>
    <div v-else>
      <Card>
        <!-- SOP 类型选择 -->
        <div style="display: flex; align-items: center; margin-bottom: 12px;">
          <span style="font-weight: 700; margin-right: 16px;">{{ $t('alarms.common.sopType') }}</span>
          <RadioGroup :key="radioGroupKey" :value="sopType" @on-change="onSopTypeChange">
            <Radio :label="0">{{ $t('alarms.common.sopTypeDefault') }}</Radio>
            <Radio :label="1">{{ $t('alarms.common.sopTypeCustom') }}</Radio>
          </RadioGroup>
        </div>

        <div style="display: flex; align-items: center; margin-bottom: 12px;">
          <span style="font-weight: 700; margin-right: 16px;">{{ priorityShowName }}</span>
          <Checkbox v-model="enableEmail" :disabled="readonly">{{ $t('alarms.common.enableEmailNotice') }}</Checkbox>
        </div>

        <!-- SOP 识别模块（仅自定义模式） -->
        <div v-if="!readonly" style="margin-bottom: 12px;">
          <div>{{ $t('configuration_management.customized_alarm_config.pleaseEnterTheProcessContent') }}</div>
          <Input
            v-model="processContent"
            type="textarea"
            :rows="3"
            :placeholder="$t('permission_configuration_management.common.pleaseInput')"
          />
          <div style="display: flex; align-items: flex-start; justify-content: space-between; margin-top: 12px;">
            <Button :loading="aiLoading" type="primary" @click="aiIdentification">{{ $t('configuration_management.customized_alarm_config.identification') }}</Button>
            <div v-if="aiTips" style="margin-left: 16px; flex: 1;">
              <span style="font-weight: 700; color: #ed4014;">*{{ $t('Bill.ResourceOptimization.prompt') }}：</span>
              <span style="color: #ed4014;">{{ aiTips }}</span>
            </div>
          </div>
        </div>

        <!-- SOP 步骤 -->
        <div class="sop-timeline-wrap" style="margin-top: 12px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;">
            <span style="font-weight: 600;">{{ $t('alarms.common.sopStepList') }}</span>
          </div>
          <div v-if="sopSteps.length === 0" style="color: #808695; padding: 8px 0;">--</div>
          <draggable v-model="sopSteps" handle=".sop-step-handle" class="sop-step-list" :disabled="readonly">
            <div v-for="(val, index) in sopSteps" :key="val.stepId || ('step-' + index)" class="sop-step-row">
              <span v-if="!readonly" class="sop-step-handle">≡</span>
              <div class="sop-step-dot">{{ index + 1 }}</div>
              <Input
                v-if="!readonly"
                v-model="sopSteps[index].stepInfo"
                type="textarea"
                :autosize="{ minRows: 1, maxRows: 3 }"
                style="flex: 1;"
                :placeholder="$t('alarms.common.stepPlaceholder')"
              />
              <div v-else style="flex: 1; line-height: 24px;">{{ sopSteps[index].stepInfo }}</div>
              <Button v-if="!readonly" size="small" type="text" style="color: #ed4014;" @click="deleteStep(index)">
                <Icon type="md-trash" />
              </Button>
            </div>
          </draggable>
          <Button v-if="!readonly && sopSteps.length < 15" type="dashed" long style="margin-top: 8px;" @click="addStep">
            <Icon type="md-add" /> {{ $t('alarms.common.addSopStep') }}
          </Button>
        </div>
      </Card>

      <!-- 邮件模板 -->
      <Card style="margin-top: 10px;">
        <div style="margin-bottom: 10px; font-weight: 600;">{{ $t('alarms.common.sopEmailTemplate') }}</div>
        <Table border :columns="emailColumns" :data="sopEmailTemplates"></Table>
      </Card>

      <div style="text-align: right; margin-top: 16px;">
        <Button @click="visible = false">{{ $t('alarms.common.cancel') }}</Button>
        <Button type="primary" style="margin-left: 8px;" :loading="saving" @click="submit">{{ $t('alarms.common.ok') }}</Button>
      </div>
    </div>
  </Modal>
</template>
<script>
import draggable from 'vuedraggable';

export default {
  name: 'AlertRuleSopModal',
  components: { draggable },
  data() {
    return {
      visible: false,
      loading: false,
      saving: false,
      alertRuleId: null,
      sopType: 0,
      priority: null,
      priorityShowName: '',
      enableEmail: true,
      sopSteps: [],
      sopEmailTemplates: [],
      editingState: {},
      processContent: '',
      aiTips: '',
      aiLoading: false,
      radioGroupKey: 0,
      emailColumns: [
        {
          title: this.$t('message.templateName'),
          key: 'templateName',
          width: 100,
          render: (h, params) => h('div', params.row.templateName)
        },
        {
          title: this.$t('configuration_management.customized_alarm_config.recipient'),
          width: 150,
          render: (h, params) => this.renderTextCell(h, params, 'recipient')
        },
        {
          title: this.$t('configuration_management.customized_alarm_config.subjectMode'),
          width: 130,
          render: (h, params) => {
            const currentMode = params.row.subjectMode === 1 ? 1 : 0;
            if (this.readonly) {
              const label = currentMode === 1
                ? this.$t('configuration_management.customized_alarm_config.subjectModeReplace')
                : this.$t('configuration_management.customized_alarm_config.subjectModeSuffix');
              return h('div', label);
            }
            return h('Select', {
              props: { value: currentMode, transfer: true },
              on: {
                'on-change': (event) => {
                  this.updateRow(params.index, { subjectMode: event });
                }
              }
            }, [
              h('Option', { props: { value: 0 } }, this.$t('configuration_management.customized_alarm_config.subjectModeSuffix')),
              h('Option', { props: { value: 1 } }, this.$t('configuration_management.customized_alarm_config.subjectModeReplace'))
            ]);
          }
        },
        {
          // 表头随主题模式切换：全部整体替换->邮件主题；全部追加后缀->邮件主题后缀；混合->两者并列
          renderHeader: (h) => {
            const templates = this.sopEmailTemplates || [];
            const suffixLabel = this.$t('configuration_management.customized_alarm_config.subjectSuffix');
            const subjectLabel = this.$t('configuration_management.customized_alarm_config.subject');
            let title;
            if (templates.length && templates.every(t => t.subjectMode === 1)) {
              title = subjectLabel;
            } else if (templates.length && templates.every(t => t.subjectMode !== 1)) {
              title = suffixLabel;
            } else {
              title = `${suffixLabel} / ${subjectLabel}`;
            }
            return h('span', title);
          },
          width: 180,
          render: (h, params) => {
            const isReplaceMode = params.row.subjectMode === 1;
            const field = isReplaceMode ? 'subject' : 'subjectSuffix';
            return this.renderTextCell(h, params, field);
          }
        },
        {
          title: this.$t('alarms.common.emailTemplate'),
          render: (h, params) => this.renderTextCell(h, params, 'templateContent', true)
        }
      ]
    };
  },
  computed: {
    readonly() {
      return this.sopType === 0;
    }
  },
  methods: {
    open(alertRuleId) {
      this.alertRuleId = alertRuleId;
      this.visible = true;
      this.loading = true;
      this.resetData();
      this.$api.alertRule.getAlertRuleSopDetail(alertRuleId).then(res => {
        const data = (res.data && res.data.data) ? res.data.data : (res.data || {});
        const hasCustomSop = data.sopType === 1 && (data.sopSteps || []).length > 0;
        // 无自定义SOP时，回退拉取默认SOP内容展示
        if (!hasCustomSop) {
          return this.loadDefaultSop({ keepLoading: true });
        }
        this.applySopData(data, 1);
      }).finally(() => {
        this.loading = false;
      });
    },
    // 拉取默认SOP内容（无自定义SOP或从自定义切换到默认时使用）
    loadDefaultSop({ keepLoading } = {}) {
      this.loading = true;
      return this.$api.alertRule.getAlertRuleDefaultSop(this.alertRuleId).then(res => {
        const data = (res.data && res.data.data) ? res.data.data : (res.data || {});
        this.applySopData(data, 0);
      }).finally(() => {
        if (!keepLoading) {
          this.loading = false;
        }
      });
    },
    // 将接口返回的SOP数据写入组件状态
    applySopData(data, sopType) {
      this.sopType = sopType;
      this.priority = data.priority;
      this.priorityShowName = data.priorityShowName || '';
      this.enableEmail = data.enableEmail !== false;
      this.sopSteps = (data.sopSteps || []).map(s => ({
        id: s.id,
        stepId: s.stepId,
        stepInfo: s.stepInfo,
        stepDetails: s.stepDetails
      }));
      this.sopEmailTemplates = (data.sopEmailTemplates || []).map(item => ({
        id: item.id,
        templateName: item.templateName,
        recipient: item.recipient || '',
        subjectMode: item.subjectMode === 1 ? 1 : 0,
        subjectSuffix: item.subjectSuffix || '',
        subject: item.subject || '',
        templateContent: item.templateContent || ''
      }));
    },
    resetData() {
      this.sopType = 0;
      this.priority = null;
      this.priorityShowName = '';
      this.enableEmail = true;
      this.sopSteps = [];
      this.sopEmailTemplates = [];
      this.editingState = {};
      this.processContent = '';
      this.aiTips = '';
      this.aiLoading = false;
    },
    // 通用文本单元格渲染（双击编辑），readonly 时只读展示
    renderTextCell(h, params, field, isTextarea) {
      const value = params.row[field];
      if (this.readonly) {
        return h('div', { style: { minHeight: '30px', lineHeight: '30px' } }, value);
      }
      const editingKey = `${params.index}_${field}`;
      const isEditing = this.editingState[editingKey];
      if (isEditing) {
        return h('Input', {
          props: isTextarea ? { type: 'textarea', rows: 3, value } : { type: 'text', value },
          on: {
            input: (event) => {
              this.updateRow(params.index, { [field]: event });
            },
            'on-blur': () => {
              this.$set(this.editingState, editingKey, false);
            }
          }
        });
      }
      return h('div', {
        on: {
          dblclick: () => {
            this.$set(this.editingState, editingKey, true);
          }
        },
        style: { cursor: 'pointer', minHeight: '30px', lineHeight: '30px' }
      }, value);
    },
    // 更新某行，保留其余字段
    updateRow(index, patch) {
      const row = this.sopEmailTemplates[index];
      this.$set(this.sopEmailTemplates, index, Object.assign({}, row, patch));
    },
    onSopTypeChange(val) {
      if (val === 0 && this.sopType === 1) {
        // 从自定义切换到默认：提示删除自定义 SOP，并拉取默认 SOP 内容
        this.$Modal.confirm({
          title: this.$t('alarms.common.sopType'),
          content: this.$t('alarms.common.switchToDefaultConfirm'),
          onOk: () => {
            this.processContent = '';
            this.aiTips = '';
            this.loadDefaultSop();
          },
          onCancel: () => {
            // 取消时强制刷新 RadioGroup，使选中值回退到原类型
            this.radioGroupKey += 1;
          }
        });
      } else {
        this.sopType = val;
      }
    },
    // SOP识别：根据流程内容生成步骤
    aiIdentification() {
      if (!this.processContent) return;
      this.aiLoading = true;
      const data = {
        userInput: this.processContent,
        emailTemplateList: (this.sopEmailTemplates || []).map(item => item.templateName)
      };
      this.$api.alertRule.sopIdentify(data).then(res => {
        if (res.data && res.data.code !== 200) {
          this.aiTips = res.data.message;
          this.sopSteps = [];
        } else {
          this.aiTips = '';
          this.sopSteps = (res.data && res.data.data) || [];
        }
      }).finally(() => {
        this.aiLoading = false;
      });
    },
    addStep() {
      if (this.sopSteps.length >= 15) return;
      this.sopSteps.push({ stepId: 'new_' + Date.now(), stepInfo: '' });
    },
    deleteStep(index) {
      this.sopSteps.splice(index, 1);
    },
    // 按主题模式裁剪邮件模板字段：追加后缀(0)不传 subject，整体替换(1)不传 subjectSuffix
    normalizeEmailTemplates(templates) {
      return (templates || []).map(item => {
        const result = Object.assign({}, item);
        if (result.subjectMode === 1) {
          delete result.subjectSuffix;
        } else {
          delete result.subject;
        }
        return result;
      });
    },
    submit() {
      if (this.sopType === 1) {
        if (this.sopSteps.length <= 0) {
          this.$Message.error(this.$t('configuration_management.customized_alarm_config.stepsCannotBeEmpty'));
          return;
        }
        const hasEmpty = this.sopSteps.some(s => !s.stepInfo || !s.stepInfo.trim());
        if (hasEmpty) {
          this.$Message.error(this.$t('configuration_management.customized_alarm_config.pleaseFillAllSteps'));
          return;
        }
      }
      // sopType=0 只传 alertRuleId 和 sopType；sopType=1 传完整数据
      const payload = this.sopType === 0
        ? { alertRuleId: this.alertRuleId, sopType: 0 }
        : {
            alertRuleId: this.alertRuleId,
            sopType: 1,
            defaultSopPriority: this.priority,
            enableEmail: this.enableEmail,
            sopSteps: this.sopSteps,
            sopEmailTemplates: this.normalizeEmailTemplates(this.sopEmailTemplates)
          };
      this.saving = true;
      this.$api.alertRule.setAlertRuleSop(payload).then(res => {
        if (res.data && res.data.code !== undefined && res.data.code !== 200) {
          this.$Message.error(res.data.message);
          return;
        }
        this.$Message.success(this.$t('configuration_management.customized_alarm_config.submitSuccess'));
        this.visible = false;
        this.$emit('saved');
      }).finally(() => {
        this.saving = false;
      });
    }
  }
};
</script>
<style scoped>
.sop-timeline-wrap {
  border: 1px solid #e8eaec;
  border-radius: 6px;
  padding: 12px 16px;
  background: #fafafa;
}
.sop-step-list {
  max-height: 400px;
  overflow-y: auto;
}
.sop-step-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}
.sop-step-row:last-child {
  border-bottom: none;
}
.sop-step-handle {
  color: #c5c8ce;
  font-size: 16px;
  cursor: grab;
  flex-shrink: 0;
}
.sop-step-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #2d8cf0;
  color: #fff;
  font-size: 12px;
  line-height: 24px;
  text-align: center;
  flex-shrink: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/monitor/common/AlertRuleSopModal.vue